/**
 * animation.ts
 * 提供基于VueUse的动画相关指令
 */

import type { App } from "vue";
import { useIntersectionObserver } from "@vueuse/core";

/**
 * 动画指令配置选项
 */
export interface AnimateDirectiveOptions {
  /** 动画名称 */
  animation?: string;
  /** 动画持续时间(秒) */
  duration?: number;
  /** 动画延迟时间(秒) */
  delay?: number;
  /** 触发动画的可见度阈值 */
  threshold?: number;
  /** 动画重复次数 */
  repeat?: number | "infinite";
  /** 是否在离开视口时重置动画 */
  reset?: boolean;
  /** 动画方向 */
  direction?: "normal" | "reverse" | "alternate" | "alternate-reverse";
  /** 动画填充模式 */
  fillMode?: "none" | "forwards" | "backwards" | "both";
  /** 动画时间函数 */
  timing?: string;
  /** 是否启用硬件加速 */
  accelerate?: boolean;
  /** 是否只触发一次 */
  once?: boolean;
}

/**
 * 设置动画指令
 * @param app Vue应用实例
 */
export function setupAnimationDirectives(app: App) {
  // v-animate 指令：元素进入视口时触发动画
  app.directive("animate", {
    mounted(el, binding) {
      // 解析指令值
      const options: AnimateDirectiveOptions =
        typeof binding.value === "object"
          ? binding.value
          : { animation: binding.value };

      // 设置默认值
      const {
        animation = "fadeIn",
        duration = 0.8,
        delay = 0,
        threshold = 0.1,
        repeat = 1,
        reset = true,
        direction = "normal",
        fillMode = "both",
        timing = "ease",
        accelerate = true,
      } = options;

      // 初始状态：隐藏元素
      el.style.opacity = "0";

      // 设置动画类
      const animateClass = `animate__${animation}`;
      el.classList.add("animate__animated");

      // 设置动画样式
      el.style.setProperty("--animate-duration", `${duration}s`);
      el.style.setProperty("--animate-delay", `${delay}s`);

      if (repeat !== 1) {
        el.style.setProperty(
          "--animate-repeat",
          repeat === "infinite" ? "infinite" : repeat.toString()
        );
      }

      // 设置其他动画属性
      el.style.animationDirection = direction;
      el.style.animationFillMode = fillMode;
      el.style.animationTimingFunction = timing;

      // 添加硬件加速
      if (accelerate) {
        el.style.transform = "translate3d(0, 0, 0)";
        el.style.backfaceVisibility = "hidden";
        el.style.perspective = "1000px";
      }

      // 使用IntersectionObserver监听元素可见性
      const { stop } = useIntersectionObserver(
        el,
        ([{ isIntersecting }]) => {
          if (isIntersecting) {
            // 元素进入视口，添加动画类
            el.style.opacity = "1";
            el.classList.add(animateClass);

            // 如果不需要重置，停止观察
            if (!reset) {
              stop();
            }
          } else if (reset) {
            el.classList.remove(animateClass);
            el.style.opacity = "0";
          }
        },
        { threshold }
      );

      // 存储停止函数，以便在卸载时调用
      el._animateStop = stop;
    },
    unmounted(el) {
      // 清理观察器
      if (el._animateStop) {
        el._animateStop();
      }
    },
  });

  console.log("[Plugin] Animation directives registered");
}
